// src/pages/farmer/FarmerDashboard.js
import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { useFarmerData } from './useFarmerData';
import api from '../../utils/api';

import OverviewPanel   from './farmerpanel/OverviewPanel';
import BrowsePanel     from './farmerpanel/BrowsePanel';
import ListingsPanel   from './farmerpanel/ListingsPanel';
import AddAnimalPanel  from './farmerpanel/AddAnimalPanel';
import OrdersPanel     from './farmerpanel/OrdersPanel';
import InquiriesPanel  from './farmerpanel/InquiriesPanel';
import AuctionsPanel   from './farmerpanel/AuctionsPanel';
import ProfilePanel    from './farmerpanel/ProfilePanel';
import EditAnimalPanel from './farmerpanel/EditAnimalPanel';

const NAV = [
  { key: 'overview',   icon: 'dashboard',    label: 'Overview'    },
  { key: 'browse',     icon: 'travel_explore', label: 'Browse'    },
  { key: 'listings',   icon: 'pets',         label: 'My Listings' },
  { key: 'add',        icon: 'add_circle',   label: 'Add Animal'  },
  { key: 'orders',     icon: 'receipt_long', label: 'Orders'      },
  { key: 'inquiries',  icon: 'forum',        label: 'Inquiries'   },
  { key: 'auctions',   icon: 'gavel',        label: 'Auctions'    },
  { key: 'profile',    icon: 'person',       label: 'Profile'     },
];

export default function FarmerDashboard() {
  const navigate         = useNavigate();
  const { user, logout, loading: authLoading } = useAuth();
  const [active, setActive] = useState(() => {
  return window.location.hash.replace('#', '') || 'overview';
  });
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [showNotifs, setShowNotifs] = useState(false);
  const [notifList,  setNotifList]  = useState([]);
  const [editingAnimal, setEditingAnimal] = useState(null);
  const [highlightAnimalId, setHighlightAnimalId] = useState(null);
  const setActiveTab = (tab) => {
    window.location.hash = tab;
    setActive(tab);
  };

  useEffect(() => {
    const onPopState = () => {
      if (!window.location.pathname.startsWith('/farmer')) return;
      const tab = window.location.hash.replace('#', '') || 'overview';
      setActive(tab);
    };
    window.addEventListener('popstate', onPopState);
    return () => window.removeEventListener('popstate', onPopState);
  }, []);

  const fetchNotifications = useCallback(async () => {
    if (!user?.id) return;
    try {
      const { data } = await api.get(`/api/farmer/notifications?userId=${user.id}`);
      setNotifList(data);
    } catch (err) {
      console.error('fetchNotifications error:', err);
    }
  }, [user?.id]);

  useEffect(() => {
    fetchNotifications();
    const interval = setInterval(fetchNotifications, 10000);
    return () => clearInterval(interval);
  }, [fetchNotifications]);

  const unreadCount = notifList.filter(n => !n.is_read).length;

  const {
    myAnimals, myOrders, myInquiries, stats, loading, error,
    fetchMyAnimals, fetchMyOrders, fetchMyInquiries,
    addAnimal, replyToInquiry, setMyOrders, 
  } = useFarmerData(user?.id);

  // ── Auth guard ─────────────────────────────────────────
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (authLoading) return;                              
    if (!user) navigate('/login');
    else if (user.role !== 'farmer') navigate('/');
  }, [user, navigate, authLoading]);                    

  // ── Load data on tab change ────────────────────────────
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (authLoading) return;                        
    if (active === 'overview')  { fetchMyAnimals(); fetchMyOrders(); }
    if (active === 'listings')  { fetchMyAnimals(); }
    if (active === 'orders')    { fetchMyOrders(); }
    if (active === 'inquiries') { fetchMyInquiries(); }
  }, [active, authLoading, fetchMyAnimals, fetchMyOrders, fetchMyInquiries]);                      

  //  Early return AFTER all hooks 
  if (authLoading) return null;                       


  const pendingInquiries = myInquiries.filter(q =>
    q.status === 'open' || q.status === 'pending'
  ).length;

  const handleLogout = () => { logout(); navigate('/'); };
  
  const handleEditAnimal = async (animalId, formData) => {
    await api.put(`/api/farmer/animals/${animalId}`, formData);
    await fetchMyAnimals();
  };

  const handleDeleteAnimal = async (animalId) => {
    if (!window.confirm('Delete this listing? This cannot be undone.')) return;
    try {
      await api.delete(`/api/farmer/animals/${animalId}`);
      await fetchMyAnimals();
    } catch (e) {
      alert(e.response?.data?.error || 'Failed to delete');
    }
  };

  // ── Panel map ──────────────────────────────────────────
  const PANELS = {
    overview:  <OverviewPanel
                 stats={stats}
                 myAnimals={myAnimals}
                 onViewAll={() => setActiveTab('listings')}
                 onAddAnimal={() => setActiveTab('add')}
                 onNavigate={setActiveTab}
               />,

    browse:    <BrowsePanel user={user} />,

    listings:  <ListingsPanel
                 animals={myAnimals}
                 loading={loading}
                 onAddAnimal={() => setActiveTab('add')}
                 onEdit={(animal) => { setEditingAnimal(animal); setActiveTab('edit'); }}
                 onDelete={handleDeleteAnimal}
                 highlightAnimalId={highlightAnimalId}
                 onHighlightDone={() => setHighlightAnimalId(null)}
               />,

    add:       <AddAnimalPanel
                 onSubmit={addAnimal}
                 onCancel={() => setActiveTab('listings')}
               />,

    edit: editingAnimal ? (
    <EditAnimalPanel
        animal={editingAnimal}
        onSubmit={handleEditAnimal}
        onCancel={() => { setEditingAnimal(null); setActiveTab('listings'); }}
      />
    ) : null,

    orders:    <OrdersPanel
                 orders={myOrders}
                 loading={loading}
                 onUpdateStatus={async (orderId, newStatus) => {
                  await fetch(`/api/farmer/orders/${orderId}/status`, {
                    method: 'PATCH',
                    headers: {
                      'Content-Type': 'application/json',
                      Authorization: `Bearer ${localStorage.getItem('token')}`
                    },
                    body: JSON.stringify({ status: newStatus })
                  });
                  setMyOrders(prev =>
                    prev.map(o => o.id === orderId ? { ...o, status: newStatus } : o)
                  );
                }}
               />,

    inquiries: <InquiriesPanel
                 inquiries={myInquiries}
                 loading={loading}
                 onReply={replyToInquiry}
               />,
    
    auctions:  <AuctionsPanel />,

    profile:   <ProfilePanel user={user} />,
  };

  // ──────────────────────────────────────────────────────
  return (
    <div className="flex min-h-screen bg-[#f4f6f4] font-body">
      {sidebarOpen && (
        <div className="fixed inset-0 bg-black/40 z-30 md:hidden"
            onClick={() => setSidebarOpen(false)} />
      )}

      {/* ── Sidebar ──────────────────────────────────── */}
      <aside className={`fixed left-0 top-0 h-screen w-60 bg-white border-r
        border-outline-variant/20 flex flex-col z-40 shadow-sm
        transition-transform duration-300
        ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'}
        md:translate-x-0`}>

        <div className="px-5 py-5 border-b border-outline-variant/10">
          <img src="/assets/elite-goats.png" alt="Elite Goats"
               className="h-10 w-auto object-contain" />
          <p className="text-[10px] font-bold text-outline uppercase tracking-widest mt-1">
            Farmer Portal
          </p>
        </div>

        <nav className="flex-1 px-3 mt-4 space-y-0.5">
          {NAV.map((item) => (
            <button key={item.key} onClick={() => { setActiveTab(item.key); setSidebarOpen(false); }}
              className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-xl
                          text-sm font-semibold transition-all ${
                active === item.key
                  ? 'bg-primary text-white shadow-sm'
                  : 'text-on-surface-variant hover:bg-surface-container hover:text-on-surface'
              }`}>
              <span className="material-symbols-outlined text-[20px]">{item.icon}</span>
              <span className="flex-1 text-left">{item.label}</span>

              {/* Inquiries badge */}
              {item.key === 'inquiries' && pendingInquiries > 0 && (
                <span className={`text-[10px] font-black px-1.5 py-0.5 rounded-full ${
                  active === item.key
                    ? 'bg-white/20 text-white'
                    : 'bg-tertiary text-white'
                }`}>
                  {pendingInquiries}
                </span>
              )}

              {/* Add badge */}
              {item.key === 'add' && (
                <span className={`text-[10px] font-black px-1.5 py-0.5 rounded-full ${
                  active === item.key
                    ? 'bg-white/20 text-white'
                    : 'bg-primary/20 text-primary'
                }`}>
                  New
                </span>
              )}
            </button>
          ))}
        </nav>

        <div className="p-3 border-t border-outline-variant/10 space-y-1">
          <button onClick={() => navigate('/', { state: { viewHome: true } })}
            className="w-full flex items-center gap-3 px-3 py-2 rounded-xl text-xs
                       font-medium text-on-surface-variant
                       hover:bg-surface-container transition-all">
            <span className="material-symbols-outlined text-[18px]">home</span>
            Back to Site
          </button>
          <button onClick={handleLogout}
            className="w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm
                       font-semibold text-red-600 hover:bg-red-50 transition-all">
            <span className="material-symbols-outlined text-[20px]">logout</span>
            Logout
          </button>
        </div>
      </aside>

      {/* ── Main ─────────────────────────────────────── */}
      <main className="flex-1 md:ml-60 min-h-screen pt-20 px-4 md:px-8 pb-8">

        {/* ── Top Header ───────────────────────────────── */}
        <header className="fixed top-0 left-0 md:left-60 right-0 z-40 h-16 bg-white/80 
          backdrop-blur-md border-b border-outline-variant/20 flex items-center 
          justify-between px-4 md:px-8 gap-4">

            {/* Hamburger — mobile only */}
            <button className="md:hidden p-2 rounded-xl hover:bg-surface-container text-secondary"
                    onClick={() => setSidebarOpen(true)}>
              <span className="material-symbols-outlined text-[24px]">menu</span>
            </button>

          <div>
            <p className="text-sm font-black text-on-surface">
              {NAV.find(n => n.key === active)?.label}
            </p>
            <p className="text-[10px] text-secondary">
              Welcome back, {user?.name?.split(' ')[0] || 'Farmer'} 👋
            </p>
          </div>

          <div className="flex items-center gap-4 flex-shrink-0">
            <button onClick={() => setActiveTab('add')}
              className="hidden md:flex items-center gap-2 px-4 py-2 rounded-full bg-primary
                         text-white font-bold text-sm hover:opacity-90
                         transition-all shadow-sm">
              <span className="material-symbols-outlined text-[18px]">add</span>
              Add Animal
            </button>

            <div className="h-7 w-px bg-outline-variant/40" />

            {/* Add bell icon HERE */}
            <div className="relative notif-wrapper">
              <button
                onClick={async () => {
                  setShowNotifs(!showNotifs);
                  if (!showNotifs && user?.id) {
                    await api.patch('/api/farmer/notifications/read', { userId: user.id });
                    fetchNotifications();
                  }
                }}
                className="relative p-2 text-on-surface-variant hover:bg-surface-container rounded-full transition-colors">
                <span className="material-symbols-outlined text-[22px]">notifications</span>
                {unreadCount > 0 && (
                  <span className="absolute top-1 right-1 w-4 h-4 bg-red-500 rounded-full
                                  text-white text-[10px] font-black flex items-center justify-center">
                    {unreadCount}
                  </span>
                )}
              </button>

              {showNotifs && (
                <div className="absolute right-0 top-12 w-80 bg-white rounded-2xl shadow-xl
                                border border-outline-variant/20 z-50 overflow-hidden">
                  <div className="px-4 py-3 border-b border-outline-variant/10 flex justify-between items-center">
                    <span className="font-bold text-sm">Notifications</span>
                    <button onClick={async () => {
                      await api.patch('/api/farmer/notifications/read', { userId: user.id });
                      fetchNotifications();
                    }} className="text-[11px] text-outline hover:text-red-500">Clear all</button>
                  </div>
                  <div className="max-h-80 overflow-y-auto">
                    {notifList.length === 0 ? (
                      <p className="text-center text-outline text-sm py-8">No notifications</p>
                    ) : notifList.map(n => (
                      <div key={n.id}
                        onClick={() => {
                          setShowNotifs(false);
                          if (n.animal_id) {
                            setHighlightAnimalId(n.animal_id);
                            setActiveTab('listings');
                          }
                        }}
                        className="px-4 py-3 border-b border-outline-variant/10 hover:bg-surface-container-low cursor-pointer">
                        <p className="text-sm font-semibold text-on-surface">{n.title}</p>
                        <p className="text-xs text-outline mt-0.5">{n.body}</p>
                        <p className="text-[10px] text-outline-variant mt-1">
                          {new Date(n.created_at).toLocaleTimeString()}
                        </p>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>

            <div className="h-7 w-px bg-outline-variant/40" />

            <div className="flex items-center gap-3">
              <div className="text-right hidden lg:block">
                <p className="text-sm font-bold text-on-surface leading-tight">
                  {user?.name || 'Farmer'}
                </p>
                <p className="text-[10px] text-secondary uppercase tracking-wider">
                  Farmer Account
                </p>
              </div>
              
              {/* Replace this section: */}
              {user?.profile_image ? (
                <img 
                  src={user.profile_image} 
                  alt="Profile"
                  className="w-9 h-9 rounded-full object-cover border-2 border-emerald-100"
                />
              ) : (
                <div className="w-9 h-9 rounded-full bg-emerald-600 flex items-center
                                justify-center text-white font-black text-sm
                                border-2 border-emerald-100">
                  {(user?.name || 'F')[0].toUpperCase()}
                </div>
              )}
            </div>
          </div>
        </header>

        {/* ── Error banner ─────────────────────────────── */}
        {error && (
          <div className="mb-6 bg-red-50 border border-red-200 text-red-700
                          px-4 py-3 rounded-xl text-sm flex items-center gap-2">
            <span className="material-symbols-outlined text-base">error</span>
            {error}
          </div>
        )}

        {/* ── Active panel ─────────────────────────────── */}
        {PANELS[active]}

      </main>
    </div>
  );
}